import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import EyeShapeIcon from "./EyeShapeIcon";
import { saveEyeShape, fetchEyeShape } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { track } from "../lib/analytics";

/**
 * EyeShapePicker
 * ──────────────
 * Onboarding custom step. Six eye-shape options + "I'm not sure yet"
 * (which writes `eye_shape="unsure", source="unsure"` so the GPT-4o
 * Vision pass becomes the source of truth on the user's next selfie).
 *
 * Why a custom step (not a regular option list)
 * ─────────────────────────────────────────────
 * Eye shape is visual — users genuinely can't pick the right answer
 * from text labels alone (especially "hooded" vs "deep-set" vs
 * "monolid"). We render a small illustrative SVG for every option so
 * the user can match what they see in the mirror.
 *
 * Persistence is fire-and-forget the moment the user picks — the
 * parent's `advance` runs after a brief 250ms beat, same as the rest
 * of onboarding's single-select questions. We DO NOT mutate
 * SessionContext.preferences here because eye_shape isn't part of
 * the encrypted style_preferences blob; it lives in its own
 * `/profile/eye-shape` resource (Fernet-encrypted column on `users`).
 */

const SHAPES = [
  { id: "hooded",     labelKey: "onboarding.steps.eyeShapeHooded",     descKey: "onboarding.steps.eyeShapeHoodedDesc" },
  { id: "monolid",    labelKey: "onboarding.steps.eyeShapeMonolid",    descKey: "onboarding.steps.eyeShapeMonolidDesc" },
  { id: "almond",     labelKey: "onboarding.steps.eyeShapeAlmond",     descKey: "onboarding.steps.eyeShapeAlmondDesc" },
  { id: "round",      labelKey: "onboarding.steps.eyeShapeRound",      descKey: "onboarding.steps.eyeShapeRoundDesc" },
  { id: "downturned", labelKey: "onboarding.steps.eyeShapeDownturned", descKey: "onboarding.steps.eyeShapeDownturnedDesc" },
  { id: "upturned",   labelKey: "onboarding.steps.eyeShapeUpturned",   descKey: "onboarding.steps.eyeShapeUpturnedDesc" },
];

const EyeShapePicker = ({ onAdvance, skipHydration = false }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  // Hydrate any previously-saved shape so returning users see their
  // current choice highlighted. Best-effort — no error UI for the
  // 401 / unauthed case (the user wouldn't be onboarding then anyway).
  //
  // `skipHydration` is passed by Onboarding when the user is on
  // `?edit=1` (the "Refresh my answers" path). In that case we
  // deliberately leave the picker blank so the user makes a fresh,
  // unbiased pick. Their old shape is still safely on the server
  // until they click a NEW shape (which fire-and-forget-saves and
  // overwrites it).
  useEffect(() => {
    if (!user || skipHydration) return;
    let cancelled = false;
    fetchEyeShape()
      .then((data) => {
        if (cancelled) return;
        if (data?.shape) setSelected(data.shape);
      })
      .catch(() => { /* unauthed / no profile yet */ });
    return () => { cancelled = true; };
  }, [user, skipHydration]);

  const choose = async (shapeId, source = "user_set") => {
    if (saving) return;
    setSelected(shapeId);
    track("eye_shape_selected", { shape: shapeId, source });
    setSaving(true);
    try {
      // For "unsure" we write the explicit unsure state so the backend
      // knows the user has SEEN the question (vs never having answered)
      // — that distinction matters when we later decide whether to
      // re-prompt them.
      await saveEyeShape(shapeId, source);
    } catch (err) {
      track("eye_shape_save_failed", { shape: shapeId });
      // Non-blocking: we still advance. The user can re-set it in
      // Settings if it didn't land server-side.
    } finally {
      setSaving(false);
      setTimeout(() => onAdvance?.(), 250);
    }
  };

  return (
    <div className="space-y-3" data-testid="eye-shape-picker">
      {SHAPES.map((s) => {
        const isSelected = selected === s.id;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => choose(s.id, "user_set")}
            disabled={saving}
            className={`w-full text-left rounded-2xl border transition-all p-4 flex items-center gap-4 ${
              isSelected
                ? "border-[#9B6A6C] bg-white shadow-[0_8px_24px_-12px_rgba(155,106,108,0.35)]"
                : "border-[#EADDD7] bg-white/60 hover:bg-white hover:border-[#D5BBB6]"
            }`}
            data-testid={`eye-shape-option-${s.id}`}
          >
            <div className="flex-shrink-0 w-[64px] h-[40px] flex items-center justify-center">
              <EyeShapeIcon shape={s.id} size={64} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-[#2C2123]">
                  {t(s.labelKey)}
                </span>
                {isSelected && (
                  <Check className="h-4 w-4 text-[#9B6A6C]" aria-hidden />
                )}
              </div>
              <p className="text-sm text-[#6B5B5D] mt-0.5 leading-snug">
                {t(s.descKey)}
              </p>
            </div>
          </button>
        );
      })}

      {/* Unsure — defers to Vision auto-detection on the next selfie */}
      <button
        type="button"
        onClick={() => choose("unsure", "unsure")}
        disabled={saving}
        className={`w-full text-left rounded-2xl border transition-all p-4 ${
          selected === "unsure"
            ? "border-[#9B6A6C] bg-white"
            : "border-dashed border-[#EADDD7] bg-white/40 hover:bg-white/70"
        }`}
        data-testid="eye-shape-option-unsure"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-[#2C2123]">
              {t("onboarding.unsureLabel")}
            </div>
            <p className="text-sm text-[#6B5B5D] mt-0.5">
              {t("onboarding.unsureDescription")}
            </p>
          </div>
          {selected === "unsure" && (
            <Check className="h-4 w-4 text-[#9B6A6C]" aria-hidden />
          )}
        </div>
      </button>
    </div>
  );
};

export default EyeShapePicker;
